import { Component, For, Index, JSX, Show } from 'solid-js';
import {
  ActionMenuItem,
  CheckboxMenuItem,
  ControlMenuItemType,
  RadioGroupMenuItem,
  RadioOption,
  ToggleMenuItem,
} from './types';
import { Icon } from '@tcweb/connect-ui';

interface ControlMenuItemProps {
  item: ControlMenuItemType;
  isMobile: boolean;
  openMenus: Set<string>;
  setOpenMenus(updater: (prev: Set<string>) => Set<string>): void;
  onNavigate(item: ControlMenuItemType): void;
  onClose?(): void;
  parentOpensUpward?: boolean;
}

export const ControlMenuItem: Component<ControlMenuItemProps> = (props) => {
  const isDisabled = (): boolean => {
    const item = props.item;
    if (item.type === 'divider') return false;
    return item.disabled ?? false;
  };

  /**
   * Renders a submenu with the given children items.
   * Extracted to avoid duplication between radio option submenus and generic children submenus.
   */
  const renderSubmenu = (children: ControlMenuItemType[]): JSX.Element => {
    const submenuVerticalClass = props.parentOpensUpward ? 'bottom-0' : 'top-0';
    return (
      <div
        class={`absolute left-full ${submenuVerticalClass} bg-base-page min-w-[150px] rounded-sm shadow-lg z-50`}
      >
        <Index each={children}>
          {(child) => (
            <ControlMenuItem
              item={child()}
              isMobile={props.isMobile}
              openMenus={props.openMenus}
              setOpenMenus={props.setOpenMenus}
              onNavigate={props.onNavigate}
              onClose={props.onClose}
              parentOpensUpward={props.parentOpensUpward}
            />
          )}
        </Index>
      </div>
    );
  };
  const getChildren = (): ControlMenuItemType[] => {
    return 'children' in props.item ? (props.item.children ?? []) : [];
  };

  const isIndeterminate = (): boolean => {
    if (
      props.item.type === 'checkbox' &&
      (props.item as CheckboxMenuItem).indeterminate
    ) {
      return (props.item as CheckboxMenuItem).indeterminate!();
    }

    const children = getChildren();
    const checkableChildren = children.filter(
      (child): child is CheckboxMenuItem | ToggleMenuItem =>
        child.type === 'checkbox' || child.type === 'toggle'
    );
    if (checkableChildren.length === 0) return false;

    const checkedCount = checkableChildren.filter(
      (child) => child.value() ?? false
    ).length;
    return checkedCount > 0 && checkedCount < checkableChildren.length;
  };

  const handleItemContainerMouseEnter = (): void => {
    if (!props.isMobile && getChildren().length > 0) {
      const itemId = props.item.id;
      props.setOpenMenus((prev) => {
        const next = new Set(prev);
        next.add(itemId);
        return next;
      });
    }
  };

  const handleItemContainerMouseLeave = (): void => {
    if (!props.isMobile) {
      const itemId = props.item.id;
      props.setOpenMenus((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  };

  const handleChevronClick = (e: MouseEvent): void => {
    e.stopPropagation();
    if (props.isMobile && getChildren().length > 0 && !isDisabled()) {
      props.onNavigate(props.item);
    }
  };

  const setValueRecursively = (
    item: ControlMenuItemType,
    value: boolean
  ): void => {
    if (item.type !== 'checkbox' && item.type !== 'toggle') return;
    item.setValue(value);
    if ('children' in item && item.children) {
      item.children.forEach((child) => setValueRecursively(child, value));
    }
  };

  const handleCheckboxClick = (e: Event): void => {
    e.stopPropagation();
    if (!isDisabled() && props.item.type === 'checkbox') {
      const checkboxItem = props.item as CheckboxMenuItem;
      const newValue = !checkboxItem.value();
      setValueRecursively(checkboxItem, newValue);
    }
  };

  const handleToggleClick = (e: Event): void => {
    e.stopPropagation();
    if (!isDisabled() && props.item.type === 'toggle') {
      const toggleItem = props.item as ToggleMenuItem;
      toggleItem.setValue(!toggleItem.value());
    }
  };

  const handleActionClick = (e: Event): void => {
    e.stopPropagation();
    if (!isDisabled() && props.item.type === 'action') {
      const actionItem = props.item as ActionMenuItem;
      actionItem.onClick();
      props.onClose?.();
    }
  };

  const handleRadioOptionClick = (
    e: Event,
    option: RadioOption,
    hasChildren: boolean
  ): void => {
    e.stopPropagation();
    if (!isDisabled() && props.item.type === 'radiogroup') {
      // On mobile, if option has children, navigate to submenu instead of selecting
      if (props.isMobile && hasChildren && option.children) {
        // Create a wrapper item with children for navigation
        // Using CheckboxMenuItem type since it supports children property
        const submenuItem: CheckboxMenuItem = {
          id: option.id,
          type: 'checkbox',
          label: option.label,
          value: () => false,
          setValue: () => {
            // No-op: this is just a navigation wrapper
          },
          children: option.children,
        };
        props.onNavigate(submenuItem);
        return;
      }

      // On desktop, if option has children, don't close - user should hover to access submenu
      if (!props.isMobile && hasChildren) {
        return;
      }

      const radioItem = props.item as RadioGroupMenuItem;
      if (radioItem.value() === option.id) {
        if (radioItem.emitWhenAlreadySelected) {
          radioItem.setValue(option.id);
          props.onClose?.();
          return;
        }
        if (radioItem.isUnselectionAllowed) {
          radioItem.setValue('');
        }
        props.onClose?.();
        return;
      }
      radioItem.setValue(option.id);
      props.onClose?.();
    }
  };

  const getItemClasses = (): string => {
    const baseClasses =
      'relative flex items-center justify-between min-w-0 flex-1';
    if (props.item.type === 'radiogroup') {
      return baseClasses;
    }
    const paddingClass = 'p-2';
    const isSelectedAction =
      props.item.type === 'action' && props.item.selected;
    const interactiveClasses = isDisabled()
      ? 'opacity-50 cursor-not-allowed'
      : isSelectedAction
        ? 'cursor-pointer bg-primary-pale'
        : 'cursor-pointer hover:bg-[var(--modus-wc-color-base-100)]';
    return `${baseClasses} ${paddingClass} ${interactiveClasses}`;
  };

  const itemContent = (
    <div class={getItemClasses()}>
      <div class="flex items-center gap-2 min-w-0 flex-1">
        <Show when={props.item.type === 'checkbox'}>
          {(() => {
            const checkboxItem = props.item as CheckboxMenuItem;
            const LabelSpan = (): ReturnType<Component> => (
              <span
                class="whitespace-nowrap flex-1 text-base-content"
                onClick={handleCheckboxClick}
                data-testid={'control-menu-dropdown-option-' + checkboxItem.id}
              >
                {checkboxItem.label}
              </span>
            );
            return (
              <>
                <modus-wc-checkbox
                  value={checkboxItem.value() ?? false}
                  indeterminate={isIndeterminate()}
                  disabled={checkboxItem.disabled}
                  on:inputChange={handleCheckboxClick}
                />
                <Show when={checkboxItem.tooltip} fallback={<LabelSpan />}>
                  <modus-wc-tooltip content={checkboxItem.tooltip}>
                    <LabelSpan />
                  </modus-wc-tooltip>
                </Show>
              </>
            );
          })()}
        </Show>

        <Show when={props.item.type === 'toggle'}>
          {(() => {
            const toggleItem = props.item as ToggleMenuItem;
            return (
              <>
                <span
                  class="whitespace-nowrap flex-1 text-base-content"
                  onClick={handleToggleClick}
                  data-testid={'control-menu-dropdown-option-' + toggleItem.id}
                >
                  {toggleItem.label}
                </span>
                <modus-wc-switch
                  size="sm"
                  value={toggleItem.value() ?? false}
                  disabled={toggleItem.disabled}
                  on:inputChange={handleToggleClick}
                />
              </>
            );
          })()}
        </Show>

        <Show when={props.item.type === 'action'}>
          {(() => {
            const actionItem = props.item as ActionMenuItem;
            return (
              <div
                class="flex items-center gap-2 w-full"
                onClick={handleActionClick}
                data-testid={'control-menu-dropdown-option-' + actionItem.id}
                aria-selected={actionItem.selected ?? false}
              >
                <Show when={actionItem.icon}>
                  <Show
                    when={typeof actionItem.icon === 'string'}
                    fallback={actionItem.icon}
                  >
                    <Icon
                      icon={actionItem.icon as string}
                      class="flex min-w-5 min-h-5 text-base-content"
                    />
                  </Show>
                </Show>
                <span class="whitespace-nowrap text-base-content">
                  {actionItem.label}
                </span>
              </div>
            );
          })()}
        </Show>

        <Show when={props.item.type === 'radiogroup'}>
          {(() => {
            const radioItem = props.item as RadioGroupMenuItem;
            return (
              <div class="flex flex-col w-full">
                <For each={radioItem.options}>
                  {(option) => {
                    const isSelected = (): boolean =>
                      radioItem.value() === option.id;
                    const hasChildren = (): boolean =>
                      (option.children?.length ?? 0) > 0;

                    const handleOptionMouseEnter = (): void => {
                      if (!props.isMobile && hasChildren()) {
                        props.setOpenMenus((prev) => {
                          const next = new Set(prev);
                          next.add(option.id);
                          return next;
                        });
                      }
                    };

                    const handleOptionMouseLeave = (): void => {
                      if (!props.isMobile && hasChildren()) {
                        props.setOpenMenus((prev) => {
                          const next = new Set(prev);
                          next.delete(option.id);
                          return next;
                        });
                      }
                    };

                    return (
                      <div
                        class="relative"
                        onMouseEnter={handleOptionMouseEnter}
                        onMouseLeave={handleOptionMouseLeave}
                      >
                        <div
                          class={`flex items-center gap-2 p-2 cursor-pointer ${
                            isSelected()
                              ? 'bg-primary-pale'
                              : 'hover:bg-[var(--modus-wc-color-base-100)]'
                          } ${radioItem.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                          data-testid={
                            'control-menu-dropdown-option-' + option.id
                          }
                          aria-selected={isSelected()}
                          onMouseDown={(e) => {
                            // Stop propagation to prevent useDropdown from closing the menu
                            e.stopPropagation();
                            e.stopImmediatePropagation();
                          }}
                          onClick={(e) =>
                            handleRadioOptionClick(e, option, hasChildren())
                          }
                        >
                          <Show when={option.icon}>
                            <Show
                              when={typeof option.icon === 'string'}
                              fallback={option.icon}
                            >
                              <Icon
                                icon={option.icon as string}
                                class="flex min-w-5 min-h-5 pr-2 text-base-content"
                              />
                            </Show>
                          </Show>
                          <span class="whitespace-nowrap text-base-content flex-1">
                            {option.label}
                          </span>
                          <Show when={option.labelRight}>
                            <span class="text-sm opacity-60 whitespace-nowrap">
                              {option.labelRight}
                            </span>
                          </Show>
                          <Show when={hasChildren()}>
                            <Icon
                              icon="tc-icon-chevron-right"
                              class="text-base-content"
                            />
                          </Show>
                        </div>
                        {/* Submenu for radio options */}
                        <Show
                          when={
                            !props.isMobile &&
                            props.openMenus.has(option.id) &&
                            hasChildren()
                          }
                        >
                          {renderSubmenu(option.children ?? [])}
                        </Show>
                      </div>
                    );
                  }}
                </For>
              </div>
            );
          })()}
        </Show>
      </div>

      <Show when={getChildren().length > 0}>
        <div
          class="cursor-pointer"
          data-testid="control-menu-item-chevron"
          onClick={handleChevronClick}
        >
          <Icon icon="tc-icon-chevron-right" class="text-base-content" />
        </div>
      </Show>
    </div>
  );

  // On desktop, use container with hover handlers
  return (
    <Show
      when={props.item.type !== 'divider'}
      fallback={<hr class="my-1 border-[var(--modus-wc-color-base-200)]" />}
    >
      <Show
        when={!props.isMobile}
        fallback={<div class="relative">{itemContent}</div>}
      >
        <div
          class="relative"
          data-testid="control-menu-item-container"
          onMouseEnter={handleItemContainerMouseEnter}
          onMouseLeave={handleItemContainerMouseLeave}
        >
          {itemContent}
          <Show
            when={
              !props.isMobile &&
              props.openMenus.has(props.item.id) &&
              getChildren().length > 0
            }
          >
            {renderSubmenu(getChildren())}
          </Show>
        </div>
      </Show>
    </Show>
  );
};
